<template>
  <main-template>
    <v-row class="mb-2">
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Novo Registro Remoto
        </h1>
        <p class="body-2 ml-8 mb-0">
          Solicite o registro de um ponto que não pôde ser feito no relógio. A solicitação passa
          pela aprovação do gestor.
        </p>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-title class="subtitle-1 font-weight-bold">Dados do registro</v-card-title>
      <v-divider />
      <v-card-text class="pt-5">
        <v-form ref="formRef">
          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                v-if="isGestor"
                v-model="colaboradorSelecionado"
                :items="colaboradores"
                :loading="carregandoColaboradores"
                item-text="name"
                return-object
                label="Colaborador"
                placeholder="Você mesmo"
                hint="Escolha um colaborador para solicitar o registro em nome dele."
                persistent-hint
                outlined
                dense
                clearable
              />
              <v-text-field
                v-else
                :value="user && user.name"
                label="Colaborador"
                outlined
                dense
                readonly
                background-color="grey lighten-4"
              />
            </v-col>

            <v-col v-if="colaboradorSelecionado" cols="12">
              <v-alert type="info" text dense class="mb-2">
                {{
                  lancamentoPeloAdmin
                    ? `O registro de ${colaboradorSelecionado.name} será lançado já aprovado por você.`
                    : `A solicitação de ${colaboradorSelecionado.name} seguirá para aprovação.`
                }}
              </v-alert>
            </v-col>

            <v-col cols="12" sm="6" class="mt-2">
              <v-menu
                v-model="menuData"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formatarData(form.data)"
                    label="Data *"
                    outlined
                    dense
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    :rules="[() => !!form.data || 'Data obrigatória']"
                  />
                </template>
                <v-date-picker
                  v-model="form.data"
                  locale="pt-BR"
                  :max="hoje"
                  @input="menuData = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6" class="mt-sm-2">
              <v-text-field
                v-model="form.hora"
                label="Horário *"
                type="time"
                outlined
                dense
                prepend-inner-icon="mdi-clock-outline"
                :rules="[(v) => !!v || 'Horário obrigatório']"
              />
            </v-col>

            <v-col cols="12">
              <div class="subtitle-2 font-weight-bold mb-2">Tipo de registro *</div>
              <v-chip-group v-model="form.tipo_batida" mandatory column active-class="tipo-ativo">
                <v-chip
                  v-for="tipo in tiposBatida"
                  :key="tipo.value"
                  :value="tipo.value"
                  outlined
                  class="tipo-chip"
                >
                  <v-icon left small>{{ tipo.icone }}</v-icon>
                  {{ tipo.text }}
                </v-chip>
              </v-chip-group>
            </v-col>

            <v-col cols="12" class="mt-3">
              <v-textarea
                v-model="form.justificativa"
                label="Justificativa *"
                placeholder="Explique por que o registro não foi feito no relógio (mín. 10 caracteres)"
                outlined
                dense
                rows="3"
                counter="500"
                :rules="[
                  (v) => !!(v && v.trim()) || 'Justificativa obrigatória',
                  (v) => !v || v.trim().length >= 10 || 'Mínimo de 10 caracteres',
                  (v) => !v || v.length <= 500 || 'Máximo de 500 caracteres',
                ]"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn text :disabled="enviando" @click="$router.go(-1)">Cancelar</v-btn>
        <v-btn
          color="primary"
          :loading="enviando"
          :disabled="!podeEnviar || enviando"
          @click="enviar"
        >
          <v-icon left small>mdi-send</v-icon>
          Enviar Solicitação
        </v-btn>
      </v-card-actions>
    </v-card>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import { obterLocalizacao } from "@/plugins/geolocalizacao";

const ROLES_GESTORES = ["admin", "super-admin", "TI", "lider"];
const ROLES_ADMIN = ["admin", "super-admin", "TI"];

function hojeIso() {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

function agoraHora() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default {
  name: "NovoRegistroRemotoPage",
  data() {
    return {
      colaboradores: [],
      colaboradorSelecionado: null,
      carregandoColaboradores: false,
      menuData: false,
      enviando: false,
      form: {
        data: hojeIso(),
        hora: agoraHora(),
        tipo_batida: "entrada",
        justificativa: "",
      },
      tiposBatida: [
        { text: "Entrada", value: "entrada", icone: "mdi-login" },
        { text: "Saída Almoço", value: "saida_almoco", icone: "mdi-silverware-fork-knife" },
        { text: "Retorno Almoço", value: "retorno_almoco", icone: "mdi-keyboard-return" },
        { text: "Saída", value: "saida", icone: "mdi-logout" },
      ],
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    // Admin, TI, super-admin e líder podem solicitar em nome de um colaborador.
    isGestor() {
      const roles = (this.user?.roles || []).map((r) => r.name);
      if (this.user?.roleName) roles.push(this.user.roleName);
      return roles.some((r) => ROLES_GESTORES.includes(r));
    },
    // Admin lançando para um colaborador (DP): entra aprovado e sem geolocalização.
    lancamentoPeloAdmin() {
      return Boolean(this.colaboradorSelecionado) && ROLES_ADMIN.includes(this.user?.roleName);
    },
    hoje() {
      return hojeIso();
    },
    podeEnviar() {
      const justificativa = this.form.justificativa?.trim() || "";
      return Boolean(
        this.form.data &&
          this.form.data <= this.hoje &&
          this.form.hora &&
          this.form.tipo_batida &&
          justificativa.length >= 10 &&
          this.form.justificativa.length <= 500
      );
    },
  },
  created() {
    if (this.isGestor) this.carregarColaboradores();
  },
  methods: {
    async carregarColaboradores() {
      try {
        this.carregandoColaboradores = true;
        const lista = await this.$services.colaboradorPontosService.listarColaboradores();
        this.colaboradores = (Array.isArray(lista) ? lista : []).filter(
          (c) => c.id !== this.user?.id
        );
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoColaboradores = false;
      }
    },
    formatarData(data) {
      if (!data) return "";
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    },
    mensagemSucesso() {
      if (this.lancamentoPeloAdmin) {
        return `Registro remoto lançado e aprovado para ${this.colaboradorSelecionado.name}.`;
      }
      if (this.colaboradorSelecionado) {
        return `Registro remoto solicitado para ${this.colaboradorSelecionado.name}.`;
      }
      return "Solicitação de registro remoto enviada com sucesso!";
    },
    async enviar() {
      if (!this.$refs.formRef.validate() || !this.podeEnviar) return;

      try {
        this.enviando = true;

        // A geolocalização valida o colaborador batendo o ponto; no lançamento pela DP não se aplica.
        let localizacao = {};
        if (!this.lancamentoPeloAdmin) {
          this.$loaderService.open("Validando segurança da solicitação...");
          try {
            localizacao = await obterLocalizacao();
          } catch (locError) {
            this.$toast.danger(
              locError.message || "Erro de validação. Verifique os acessos e tente novamente."
            );
            return;
          } finally {
            this.$loaderService.close();
          }
        }

        const payload = {
          data: this.form.data,
          hora: this.form.hora,
          tipo_batida: this.form.tipo_batida,
          justificativa: this.form.justificativa.trim(),
          ...localizacao,
        };
        if (this.colaboradorSelecionado) {
          payload.colaborador_id = this.colaboradorSelecionado.id;
        }

        this.$loaderService.open("Processando e salvando registro...");
        await this.$services.colaboradorPontosService.solicitarBatidaRemota(payload);
        this.$toast.success(this.mensagemSucesso());
        this.$router.push({ name: "colaboradores.batidasRemotas" });
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$loaderService.close();
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
.tipo-chip {
  border-radius: 10px !important;
}

.tipo-ativo {
  border: 2px solid var(--brand) !important;
  background-color: var(--brand-soft) !important;
  color: var(--brand) !important;
  font-weight: 600;
}
</style>
